import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";
import { pricelistCaseReminderBundleSchema } from "./shapes";

/**
 * Query Due Pricelist Case Follow-ups
 *
 * Finds every OPEN pricelist update case (GEN-2524) whose "Set next step"
 * follow-up date has come due, is past its reminder gate, and whose companion
 * task is assigned to a user — then routes each to that assignee. Only
 * explicitly parked, owned cases are reminded; an unassigned (or
 * agent-assigned) due case never notifies and stays eligible. Emits one bundle
 * per recipient carrying only their own cases, as the `reminders` array. Wire
 * that to a single-object input on the next step to fan out one notification
 * per recipient.
 *
 * Cases included in the result have their reminder gate closed as part of
 * this step. By default a case is reminded ONCE per follow-up (GEN-2967):
 * setting a new follow-up re-arms it. A `cooldownDays` value instead repeats
 * the reminder every that many days while the follow-up stays overdue.
 *
 * ## How it runs
 *
 * Every organization gets the system-managed "Pricelist Case Follow-up
 * Reminders" workflow, installed on boot (`seedPricelistCaseReminders.ts`):
 *
 *   Schedule Trigger (every 15 minutes)
 *     -> Query Due Pricelist Case Follow-ups   (`cooldownDays`, default once)
 *        `reminders` (array)  ->  fan-out
 *     -> Prepare Pricelist Case Reminder       (`fallbackChannels`)
 *     -> Send Announcement (v2, input-driven)  (`senderKind` / `senderMailbox`
 *                                               when email is in play)
 *
 * The array-to-single-object edge on `reminders` is what makes the fan-out
 * happen — one child run, and so one digest, per assignee.
 */
export const queryDuePricelistCaseFollowUpsStep = defineStepConfig({
  type: "query_due_pricelist_case_follow_ups",
  name: "Query Due Pricelist Case Follow-ups",
  description:
    "Find every open pricelist update case whose Set-next-step follow-up " +
    "date has come due and whose task is assigned to a user, and route it to " +
    "that assignee. Unassigned cases and cases already reminded are skipped.",
  category: "Data Access",
  libraryModule: "ItemMaster",

  // No data inputs — just the implicit flow signal from the schedule trigger.
  inputSchema: z.object({}),

  outputSchema: z.object({
    reminders: z
      .array(pricelistCaseReminderBundleSchema)
      .describe(
        "One bundle per assignee, each with the cases whose follow-up came " +
          "due for them. Connect to a single-object input (e.g. Prepare " +
          "Pricelist Case Reminder) to fan out one notification per recipient.",
      ),
    recipientCount: z
      .number()
      .int()
      .nonnegative()
      .describe("Number of assignees who will be notified."),
    caseCount: z
      .number()
      .int()
      .nonnegative()
      .describe("Number of distinct due cases in the digest."),
  }),

  configSchema: z.object({
    cooldownDays: z
      .number()
      .int()
      .positive()
      .nullable()
      .default(null)
      .describe(
        "Leave empty to remind once per follow-up. A number repeats the " +
          "reminder every that many days while the follow-up stays overdue.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "60 seconds",
    retry: {
      maximumAttempts: 3,
      backoffCoefficient: 2,
      initialInterval: "5 seconds",
    },
  },
});
